import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import * as React from 'react';
import { useForkRef, useIsomorphicLayoutEffect, useEventCallback } from '@cian/react-utils';
import { useIsFocusVisible } from '../../../../../internal/hooks';
import { ownerDocument } from '../../../../../internal/utils';
import { valueToPercent } from '../../helpers';
import { useSliderValueThumbsKeyDownEvent } from './useSliderValueThumbsKeyDownEvent';
export function useSliderValueThumbs(_ref) {
  var props = _ref.props,
    valueState = _ref.valueState,
    marks = _ref.marks;
  var disabled = props.disabled,
    min = props.min,
    max = props.max;
  var sliderRef = React.useRef(null);
  // We can't use the :active browser pseudo-classes.
  // - The active state isn't triggered when clicking on the rail.
  // - The active state isn't transferred when inversing a range slider.
  var _React$useState = React.useState(-1),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    activeIndex = _React$useState2[0],
    setActiveIndex = _React$useState2[1];
  var _React$useState3 = React.useState(-1),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    openIndex = _React$useState4[0],
    setOpenIndex = _React$useState4[1];
  var _React$useState5 = React.useState(-1),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    focusVisibleIndex = _React$useState6[0],
    setFocusVisibleIndex = _React$useState6[1];
  var _useIsFocusVisible = useIsFocusVisible(),
    isFocusVisibleRef = _useIsFocusVisible.isFocusVisibleRef,
    handleFocusVisible = _useIsFocusVisible.onFocus,
    handleBlurVisible = _useIsFocusVisible.onBlur,
    focusVisibleRef = _useIsFocusVisible.ref;
  var handleSliderRef = useForkRef(sliderRef, focusVisibleRef);
  var handleKeyDown = useSliderValueThumbsKeyDownEvent({
    props: props,
    valueState: valueState,
    setFocusIndex: setFocusIndex,
    setFocusVisibleIndex: setFocusVisibleIndex,
    marks: marks
  });
  var handleFocus = useEventCallback(function (event) {
    var index = Number(event.currentTarget.getAttribute('data-index'));
    handleFocusVisible(event);
    if (isFocusVisibleRef.current === true) {
      setFocusVisibleIndex(index);
    }
    setOpenIndex(index);
  });
  var handleBlur = useEventCallback(function (event) {
    handleBlurVisible(event);
    if (!isFocusVisibleRef.current) {
      setFocusVisibleIndex(-1);
    }
    setOpenIndex(-1);
  });
  var handleMouseOver = useEventCallback(function (event) {
    var index = Number(event.currentTarget.getAttribute('data-index'));
    setOpenIndex(index);
  });
  var handleMouseLeave = useEventCallback(function () {
    setOpenIndex(-1);
  });
  if (disabled && focusVisibleIndex !== -1) {
    if (focusVisibleIndex !== -1) {
      setFocusVisibleIndex(-1);
    }
    if (activeIndex !== -1) {
      setActiveIndex(-1);
    }
  }
  useIsomorphicLayoutEffect(function () {
    var slider = sliderRef.current;
    var _ownerDocument = ownerDocument(slider),
      activeElement = _ownerDocument.activeElement;
    if (disabled && slider && activeElement && slider.contains(activeElement)) {
      // This is necessary because Firefox and Safari will keep focus
      // on a disabled element:
      // https://codesandbox.io/s/mui-pr-22247-forked-h151h?file=/src/App.js
      activeElement.blur();
    }
  }, []);
  function setFocusIndex(index) {
    var slider = sliderRef.current;
    var _ownerDocument2 = ownerDocument(slider),
      activeElement = _ownerDocument2.activeElement;
    if (slider) {
      if (!slider.contains(activeElement) || activeElement && Number(activeElement.getAttribute('data-index')) !== index) {
        var thumbElement = slider.querySelector("[role=\"slider\"][data-index=\"".concat(index, "\"]"));
        if (thumbElement) {
          thumbElement.focus();
        }
      }
    }
    setActiveIndex(index);
  }
  var thumbs = valueState.values.map(function (value, index) {
    var active = activeIndex === index;
    var open = openIndex === index || active;
    var focusVisible = focusVisibleIndex === index;
    var percent = valueToPercent(value, min, max);
    return {
      open: open,
      active: active,
      focusVisible: focusVisible,
      value: value,
      percent: percent,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onMouseOver: handleMouseOver,
      onMouseLeave: handleMouseLeave,
      onKeyDown: handleKeyDown
    };
  });
  return {
    sliderRef: handleSliderRef,
    setOpenIndex: setOpenIndex,
    setFocusIndex: setFocusIndex,
    setActiveIndex: setActiveIndex,
    thumbs: thumbs
  };
}